// Section 4 phase 3 and Section 8: production. Each working, eligible person produces their
// job's evaluated output (base yield x strength curve x skill x tool x happiness x traits x
// season x land, all through the modifier pipeline); the job's produce hook turns that into
// deltas. Then services from buildings, construction, the seed reserve, tool wear and job risks.
import { isWorking, peopleSorted } from '../../core/queries';
import type {
  Delta,
  Factor,
  JobId,
  Person,
  Phase,
  PhaseContext,
  Resource,
  SeasonReport,
  ServiceKind,
} from '../../core/types';
import { recomputeCapacity } from '../shared/capacity';
import { applyDelta } from '../shared/deltas';
import { seal } from '../shared/seal';
import { workBlock } from '../shared/eligibility';
import { valueOf, num } from '../shared/value';
import { construct } from './build';
import {
  losePlots,
  plant,
  reserveSeed,
  startPlanting,
  totalPlots,
  type PlantingTally,
} from './farming';
import { rollRisks } from './risks';
import { settleTools } from '../shared/tools';

const SERVICES: readonly ServiceKind[] = [
  'buildPoints',
  'healingSlots',
  'teachingSeats',
  'careSlots',
  'safetyPoints',
];

interface JobTally {
  amount: number;
  workers: number;
  factors: Map<string, Factor>;
}

function tallyFactors(tally: JobTally, factors: readonly Factor[]): void {
  for (const f of factors) {
    const key = f.source;
    const row = tally.factors.get(key);
    if (row === undefined)
      tally.factors.set(key, { source: f.source, op: f.op, value: f.delta, delta: f.delta });
    else {
      row.value += f.delta;
      row.delta += f.delta;
    }
  }
}

function primaryAmount(ctx: PhaseContext, job: JobId, deltas: readonly Delta[]): number {
  const out = ctx.registry.jobs[job].output;
  let total = 0;
  for (const d of deltas) {
    if (out.kind === 'stock' && d.kind === 'stock' && d.resource === out.resource && d.amount > 0)
      total += d.amount;
    if (out.kind === 'service' && d.kind === 'service' && d.service === out.service)
      total += d.amount;
  }
  return total;
}

function work(
  ctx: PhaseContext,
  worker: Person,
  tallies: Map<JobId, JobTally>,
  planting: PlantingTally,
  outputs: SeasonReport['outputs'],
): void {
  const job = worker.job;
  const evaluation = valueOf(ctx, { kind: 'output', personId: worker.id, jobId: job });
  const deltas = ctx.registry.jobs[job].produce(ctx.live, worker, evaluation.value);
  for (const d of deltas) {
    if (d.kind === 'plant') plant(ctx, d.personId, d.plots, planting);
    else applyDelta(ctx, d);
  }
  const amount = primaryAmount(ctx, job, deltas);
  outputs[worker.id] = { jobId: job, amount };
  ctx.draft.lastWorkedTurn[job] = ctx.season.turn;
  const tally = tallies.get(job) ?? { amount: 0, workers: 0, factors: new Map<string, Factor>() };
  tally.amount += amount;
  tally.workers += 1;
  tallyFactors(tally, evaluation.factors);
  tallies.set(job, tally);
}

function reportProduction(ctx: PhaseContext, tallies: Map<JobId, JobTally>): void {
  for (const [job, t] of [...tallies.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const out = ctx.registry.jobs[job].output;
    if (out.kind === 'none') continue;
    const resource: Resource | ServiceKind = out.kind === 'stock' ? out.resource : out.service;
    ctx.report.add({
      kind: 'production',
      jobId: job,
      resource,
      amount: t.amount,
      workers: t.workers,
      factors: [...t.factors.values()],
    });
  }
}

function warnings(ctx: PhaseContext, farmersIdle: number): void {
  const last = ctx.draft.lastWorkedTurn.toolmaker;
  const since = last === undefined ? ctx.season.turn : ctx.season.turn - last;
  if (since >= ctx.cfg.tools.noToolmakerWarnSeasons)
    ctx.report.add({ kind: 'warning', code: 'no-toolmaker', seasons: since });
  if (farmersIdle > 0)
    ctx.report.add({ kind: 'warning', code: 'farmers-without-plots', amount: farmersIdle });
}

export const productionPhase: Phase = {
  id: 'production',
  reads: [
    'people',
    'buildings',
    'stocks',
    'policies',
    'plantedPlots',
    'buildQueue',
    'toolOverrides',
  ],
  writes: [
    'stocks',
    'plantedPlots',
    'fieldsPlanted',
    'seedReserved',
    'buildQueue',
    'buildings',
    'toolDurability',
    'lastWorkedTurn',
    'people',
    'currentReport',
  ],
  projection: 'run',
  run(ctx) {
    const season = ctx.season.season;
    // Nothing has changed yet this phase: evaluate on the committed state (Section 2 budget).
    recomputeCapacity(ctx, ctx.draft);
    if (season === 'spring') startPlanting(ctx);
    if (season === 'summer' || season === 'autumn') losePlots(ctx);

    const tallies = new Map<JobId, JobTally>();
    const planting: PlantingTally = { plots: 0, seedUsed: 0, fromFood: 0, refused: 0 };
    // Workers are read from the committed state (decide has applied this season's assignments);
    // produce hooks return deltas, so no person needs drafting just to be read.
    const workers = peopleSorted(ctx.state).filter(
      (p) => p.job !== 'rest' && isWorking(p) && workBlock(ctx, p, p.job) === null,
    );
    let farmersIdle = 0;
    // Built here and put into the report once (Section 2 budget: one sealed write, not one draft
    // write per worker); nothing reads this season's outputs before the loop ends.
    const outputs: SeasonReport['outputs'] = {};
    for (const worker of workers) {
      if (
        worker.job === 'farmer' &&
        season !== 'spring' &&
        season !== 'winter' &&
        (ctx.draft.plantedPlots[worker.id] ?? 0) === 0
      )
        farmersIdle++;
      work(ctx, worker, tallies, planting, outputs);
    }
    const report = ctx.report.current;
    report.outputs = seal(ctx, { ...ctx.state.currentReport?.outputs, ...outputs });
    if (season === 'spring' && (planting.plots > 0 || planting.refused > 0)) {
      ctx.report.add({
        kind: 'planted',
        plots: planting.plots,
        seedUsed: planting.seedUsed,
        fromFood: planting.fromFood,
      });
      if (planting.refused > 0 && ctx.draft.fieldsPlanted < totalPlots(ctx)) {
        ctx.report.add({
          kind: 'warning',
          code: 'seed-not-reserved',
          amount: planting.refused * ctx.cfg.jobs.farmer.seedPerPlot,
        });
      }
    }
    reportProduction(ctx, tallies);

    const services = ctx.report.current.services;
    for (const s of SERVICES) {
      // Buildings, jobs and effects are unchanged until construction below, so the committed
      // state gives the same services without drafting anyone (Section 2 budget).
      const extra = num(ctx, { kind: 'service', service: s });
      if (extra !== 0) services[s] = (services[s] ?? 0) + extra;
    }
    construct(ctx, services.buildPoints ?? 0);
    if (season === 'autumn') reserveSeed(ctx);

    settleTools(ctx);
    warnings(ctx, farmersIdle);
    rollRisks(ctx, workers);
    return 'done';
  },
};
